/**
 * Microsoft Authentication Library (MSAL) Configuration
 * Azure Entra ID authentication setup for React application
 * 
 * SINGLE SOURCE OF TRUTH: All Azure Entra configurations come from .env file
 * - Frontend App Client ID: REACT_APP_AZURE_CLIENT_ID
 * - Tenant ID: REACT_APP_AZURE_TENANT_ID
 * - Backend API scope: REACT_APP_API_SCOPE
 */

import { Configuration, PopupRequest, RedirectRequest, LogLevel } from '@azure/msal-browser';
import { logger } from '../utils/logging/logger';
import { getAzureClientId, getAzureTenantId, getApiScope } from './index';

// MSAL Configuration - Using values from .env file
export const msalConfig: Configuration = {
  auth: {
    clientId: getAzureClientId(), // From .env: REACT_APP_AZURE_CLIENT_ID
    // authority: `https://login.microsoftonline.com/${getAzureTenantId()}`, // From .env: REACT_APP_AZURE_TENANT_ID
    authority: `https://login.microsoftonline.com/${getAzureTenantId()}/v2.0`,
    redirectUri: window.location.origin, // Use current domain dynamically
    postLogoutRedirectUri: window.location.origin + '/login', // Use current domain dynamically
    navigateToLoginRequestUrl: true, // Enable proper redirect handling
  },
  cache: {
    cacheLocation: 'sessionStorage', // Use sessionStorage like working test app
    storeAuthStateInCookie: false, // Keep as false for security
  },
  system: {
    loggerOptions: {
      loggerCallback: () => {
        // Completely silent - no logging whatsoever
      },
      piiLoggingEnabled: false,
      logLevel: LogLevel.Error, // Set to most restrictive level
    },
  },
};

// Debug logging for development
if (process.env.NODE_ENV === 'development') {
  logger.auth('MSAL Config Debug', {
    clientId: msalConfig.auth.clientId,
    authority: msalConfig.auth.authority,
    redirectUri: msalConfig.auth.redirectUri,
    postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
    currentOrigin: window.location.origin,
    currentHref: window.location.href,
    NODE_ENV: process.env.NODE_ENV,
    REACT_APP_ENVIRONMENT: process.env.REACT_APP_ENVIRONMENT
  });
}

// Scopes for accessing different APIs - Dual token strategy
export const apiScopes = {
  graph: ['User.Read', 'GroupMember.Read.All', 'openid', 'profile', 'email'], // Graph API token
  backend: [getApiScope()], // Backend API token from .env: REACT_APP_API_SCOPE
};

// Login request with both Graph API and Backend API scopes
export const loginRequest: RedirectRequest = {
  scopes: [
    ...apiScopes.graph,  // Graph API scopes for user info and groups
    ...apiScopes.backend  // Backend API scope for API access
  ],
  prompt: 'select_account', // Let user choose account
  // Removed domain_hint to allow all account types
};

// Graph API token request for user info and groups
export const graphTokenRequest: PopupRequest = {
  scopes: apiScopes.graph,
};

// Backend API token request for API calls
export const backendTokenRequest: PopupRequest = {
  scopes: apiScopes.backend,
};

// Graph API scopes for getting user groups
export const graphScopes = {
  userRead: ['User.Read'],
  groupsRead: ['GroupMember.Read.All'],
};

// Configuration for Microsoft Graph API calls
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphGroupsEndpoint: 'https://graph.microsoft.com/v1.0/me/memberOf'
};

// Development helper function
export const getActiveConfig = (): Configuration => {
  return msalConfig;
};
